<template lang="pug">
  div#questionlist
    // Upper Control Panel Buttons
    el-row#control-panel(:style="{ display: (showBuilder || showImport) ? 'none' : 'block' }")
      el-col(:span="12")
        el-switch(
          v-model="showPreview"
          :disabled="questionList.length <= 0"
          active-color="#13ce66"
          active-text="Show Question Details") 
      el-col(:span="12")
        div(style="float: right")
          el-button(
            v-if="isDragged"
            class="primary-button"
            @click="saveQuestionOrder"
            icon="el-icon-document") 
              small Save    
          el-button(
            class="normal-button"
            @click="showLibraryModal=true; libraryType=null"
            icon="el-icon-download") 
              small Import    
          el-button(
            class="primary-button"
            @click="showTypeModal=true; questionType=0"
            icon="el-icon-edit") 
              small Create    

    // Question Builder and Items placeholder
    template()
      question-type-modal(
        :visible-prop="showTypeModal"
        @close="showTypeModal = false"
        @formCancelClicked="showTypeModal = false"
        @createQuestion="createQuestion"
      )

      library-selector-modal(
        :visible-prop="showLibraryModal"
        @close="showLibraryModal = false"
        @formCancelClicked="showLibraryModal = false"
        @selectLibrary="showLibrary"
      )

      question-builder(
        v-if="showBuilder"
        :pageView="pageview"
        :questionData="question"
        @onProcessing="onProcessing"
        @formCancelClicked="showBuilder = false"
        @onQuestionSaved="updateQuestion")

      question-library(
        v-else-if="showImport && libraryType != null"
        :libraryType="libraryType"
        @onProcessing="onProcessing"
        @formCancelClicked="showImport = false"
        @showLibraryModal="showLibraryModal = true"
        @onQuestionImported="refreshList")

      el-row#question-items(v-else)
        div#placeholder-empty(v-if="questionList.length <= 0")
          p#title Empty Questions List 
        // Questions list items
        div(v-else)
          draggable(
            v-if="!isListLoading"
            v-model="questionList" v-bind="dragOptions" :move="()=>{isDragged=true}")
            div(v-for="item in questionList")
              question-placeholder(
                :id="item.id" 
                :questionData="item"
                :showPreview="showPreview"
                :selectOnly="false"
                @hideItem="deleteQuestion" 
                @editQuestion="editQuestion")
</template>

<script>
  import QuestionPlaceholder from '@/components/instructor/Question/Placeholder/QuestionPlaceholder'
  import QuestionBuilder from '@/components/instructor/Question/QuestionBuilder'
  import LibrarySelectorModal from '@/components/instructor/Library/LibrarySelectorModal'
  import QuestionLibrary from '@/components/instructor/Question/QuestionLibrary'
  import QuestionTypeModal from '@/components/instructor/Question/Components/QuestionTypeModal'
  import utilMixin from '@/components/mixins/utilMixin'
  import draggable from 'vuedraggable'
  
  export default {
    name: 'QuestionList',

    components: {
      QuestionPlaceholder,
      QuestionBuilder,
      LibrarySelectorModal,
      QuestionLibrary,
      QuestionTypeModal,
      draggable,
    },

    mixins: [utilMixin],

    props: {
      quizData: {
        type: Object,
        required: true,
      },

      placeholderList: {
        type: Array,
        default: function() {
          return [
            {
              id: 1,
            },
          ]
        },
      },
    },

    data () {
      return {
        showBuilder: false,
        showPreview: false,
        showImport: false,
        showLibraryModal: false,
        showTypeModal: false,
        pageview: '',
        question: {},
        questionList: [],   
        isDragged: false,   
        isListLoading: false,
        libraryType: null,
        questionType: 0,
      }
    },

    computed: {
      dragOptions() {
        return {
          animation: 150,
          group: "questions",
          ghostClass: "ghost",
        }
      },
    },

    watch: {
      quizData() {
        this.refreshList()
      },
    },    

    methods: {
      deleteQuestion(id) {
        if (this.questionList.length > 0) {
          this.$emit('onProcessing', true)
          this.isListLoading = true

          this.axios.delete('/api/quiz/' + this.quizData.id + '/question/' + id)
            .then((result) => {
              for (let idx=0; idx<this.questionList.length; idx += 1) {
                if ( this.questionList[idx].id === id) {
                  this.questionList.splice(idx,1)
                  break
                }
              }
              this.isListLoading = false
              this.refreshOrder()
              this.mNotification( {message: 'Your Question has been deleted.'})
            })
            .catch((error) => {
              this.isListLoading = false
              this.mNotification({error: error, message: 'Your Question was not deleted.'})
            })
        } else {
          this.placeholderList.pop()
        }
      },

      createQuestion(type) {
        if (type) {
          this.question = {
            title: 'Question ' + (this.questionList.length +1),
            text: '',
            lefthand: '',
            righthand: '',
            correct_answer: (this.questionList.length > 0) ? '' : '1',
            allow_math_equivs: true,
            score: 1,
            item_order: this.questionList.length,
            type_id: type,
            custom_symbols: [],
          }
          this.showBuilder = true
          this.showTypeModal = false
          this.pageview = 'create'
        }
      },   

      editQuestion(id) {
        this.showBuilder = !this.showBuilder
        this.pageview = 'edit'

        for (let i=0; i<this.questionList.length; i += 1) {
          if (this.questionList[i].id === id) {
            this.question = this.questionList[i]
            break
          }
        }
      },      

      saveQuestionOrder() {
        this.$emit('onProcessing', true)
        
        let revisedOrder = []
        for (let idx=0; idx<this.questionList.length; idx += 1) {
          if ( this.questionList[idx].item_order != idx) {
            revisedOrder.push({id:this.questionList[idx].id,item_order:idx})
          }
        }

        this.axios.post('/api/quiz/' + this.quizData.id + '/question/updateorder', {params: {revisedOrder: revisedOrder}})
         .then(() => {
            this.refreshOrder()
            this.isDragged = false
            this.mNotification( {message: 'Revised question order has been saved.'})
          })
          .catch((error) => {
            this.mNotification({error: error, message: 'Revised question order has not been saved.'})
          })
      },

      refreshOrder() {
         for (let idx=0; idx<this.questionList.length; idx += 1) {
            this.questionList[idx].item_order = idx
          }
      },

      updateQuestion() {
        let isNew = true
        this.question.isSelected =true
        this.questionList.forEach((item,index)=> {
          if ( this.question.id === item.id ) {
            this.questionList[index] = this.question
            isNew = false
          } else {
             this.questionList[index].isSelected = false
          }
        })
        if ( isNew ) {
          this.questionList.push(this.question)
        }
        this.showBuilder = false
      },

      refreshList() {
        this.isListLoading = true
        this.$emit('onProcessing', true)
        this.axios.get(`/api/quiz/${this.quizData.id}/question`)
          .then((result) => {
            this.questionList = []
            result.data.object.forEach((item, index) => {
              this.questionList.push(item)
            })
            this.$emit('onProcessing', false)
            this.isListLoading = false
            this.showBuilder = false
          })
          .catch((error) => {
            this.isListLoading = false
            this.$store.state.quizData = {}
            this.mNotification({error: error, message: 'Your Question list was not fully retrieved.'})
          })
      },

      showLibrary(type) {
        this.libraryType = type
        this.showLibraryModal = false
        this.showImport = true
      },

      onProcessing(flag) {
        this.$emit('onProcessing', flag)
      },  

    },
  } 
</script>

<style module>
  #questionlist {
    width: 100%;
    margin: auto;
  }

  #control-panel {
    display: flex; 
    justify-content: flex-end; 
    margin: 1% 2% 2% 2%;
  }

  #question-items {
    display: flex; 
    flex-direction: column; 
    justify-content: flex-start;
  }

  .ghost {
    opacity: 0.5;
    background: #fff;
  }
</style>
